import { Check, ChevronDown, Search, X, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from './dropdown';
import { CONTROLE_BARRA } from './ui';

/**
 * Seletor de opção — o dropdown-padrão do produto.
 *
 * **Substitui o `<select>` nativo em toda barra de ferramentas e filtro.** O
 * nativo é desenhado pelo sistema operacional: no Windows ele abre uma lista
 * branca, quadrada e estreita, que ignora o tema, ignora os tokens e corta
 * texto longo — "Sociedade em Comandita Simples" não cabe.
 *
 * A forma vem do `icon-select.tsx` do isotec-react, que é o nosso parâmetro de
 * layout. O que ela entrega e o nativo não:
 *
 *  - **ícone por opção**, para o olho achar antes de ler;
 *  - **linha de apoio** (`descricao`), para explicar sem tooltip;
 *  - **largura própria do painel**, independente do gatilho — o gatilho cabe
 *    na barra, o painel cabe no texto;
 *  - **campo de consulta dentro do painel** a partir de 8 opções;
 *  - **tema e tokens**, como o resto do produto.
 *
 * Teclado e leitor de tela: o `Dropdown` já fecha no `Esc` e no clique fora, e
 * cada opção é um `<button>` de verdade, alcançável por `Tab`.
 */

export interface OpcaoSeletor<T extends string> {
  valor: T;
  rotulo: string;
  /** Linha de apoio no painel. Não aparece no gatilho. */
  descricao?: string;
  /** Ícone da opção. Sem ele, a opção alinha com as que têm. */
  Icone?: LucideIcon;
}

/**
 * A partir de quantas opções o painel ganha campo de consulta.
 *
 * Oito é onde a lista deixa de caber num relance: até sete o olho varre mais
 * rápido do que a mão digita, e um campo ali só rouba a primeira linha do
 * painel. Passando disso, procurar vira o gesto.
 */
const MINIMO_PARA_BUSCA = 8;

/**
 * Sem acento e sem caixa: quem digita "comandita" acha "Comandita".
 *
 * `\p{Mn}` (Mark, nonspacing) e não um intervalo de code points escrito com
 * os caracteres literais: literal depende do encoding do arquivo sobreviver a
 * todo editor, `git` e pipeline do caminho — e quando não sobrevive, a função
 * para de tirar acento sem avisar. A classe nomeada é ASCII no fonte e diz o
 * que faz.
 */
function planificar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase();
}

/**
 * O conteúdo do painel, num componente PRÓPRIO — e não inline no `Dropdown`.
 *
 * O `Dropdown` só monta o painel quando abre, então o termo digitado nasce
 * vazio a cada abertura (um termo guardado da vez anterior esconderia opções
 * sem que ninguém tivesse digitado nada agora, e a lista pareceria mais curta
 * do que é) e o `autoFocus` dispara de verdade — num nó que o React
 * reaproveitasse, ele só valeria na primeira vez.
 */
function PainelOpcoes<T extends string>({
  opcoes,
  valor,
  aoEscolher,
  rotulo,
  comBusca,
  placeholderBusca,
  fechar,
}: {
  opcoes: readonly OpcaoSeletor<T>[];
  valor: T;
  aoEscolher: (valor: T) => void;
  rotulo: string;
  comBusca: boolean;
  placeholderBusca: string;
  fechar: () => void;
}): JSX.Element {
  const [termo, setTermo] = useState('');
  const refBusca = useRef<HTMLInputElement>(null);

  /*
   * O foco tem DUAS armadilhas em série, e as duas falham em silêncio.
   *
   * A primeira: `autoFocus` perde para o foco que o clique dá ao gatilho — o
   * atributo pede antes, o navegador foca o botão depois. Por isso o pedido
   * mora num efeito, que roda após o commit.
   *
   * A segunda: o `Dropdown` renderiza o painel com `visibility: hidden` até
   * medir onde ele cabe (senão o menu aparece no canto e pula). E `.focus()`
   * num elemento invisível **não faz nada e não avisa** — o efeito rodava,
   * parecia certo, e o campo continuava sem foco. O `requestAnimationFrame`
   * espera o frame em que a medição já pintou o painel.
   */
  useEffect(() => {
    if (!comBusca) return;
    const quadro = requestAnimationFrame(() => refBusca.current?.focus());
    return () => cancelAnimationFrame(quadro);
  }, [comBusca]);

  // O termo é planificado UMA vez por digitação, e não uma vez por opção: com
  // a normalização dentro do laço, cada tecla refazia `NFD` na lista inteira.
  const filtradas = useMemo(() => {
    const t = planificar(termo.trim());
    if (!t) return opcoes;
    return opcoes.filter(
      (o) =>
        planificar(o.rotulo).includes(t) || planificar(o.descricao ?? '').includes(t),
    );
  }, [opcoes, termo]);

  return (
    <>
      {comBusca && (
        <div className="border-tinta-100 mb-1 border-b px-1 pb-1">
          <div className="relative">
            <Search
              className="text-tinta-500 pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <input
              ref={refBusca}
              type="text"
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => {
                // Enter escolhe quando sobrou uma só: exigir o clique seria
                // pedir que a pessoa repita o que já disse.
                if (e.key === 'Enter' && filtradas.length === 1 && filtradas[0]) {
                  e.preventDefault();
                  aoEscolher(filtradas[0].valor);
                  fechar();
                }
              }}
              placeholder={placeholderBusca}
              aria-label={`Consultar em ${rotulo}`}
              className="border-tinta-100 bg-campo text-tinta-900 placeholder:text-tinta-500 focus:border-marca-500 h-9 w-full rounded-md border pr-8 pl-8 text-sm outline-none"
            />
            {termo && (
              <button
                type="button"
                onClick={() => {
                  setTermo('');
                  refBusca.current?.focus();
                }}
                aria-label="Limpar a consulta"
                className="text-tinta-500 hover:text-tinta-900 absolute top-1/2 right-1.5 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded"
              >
                <X className="size-3.5" aria-hidden />
              </button>
            )}
          </div>
        </div>
      )}

      <div role="listbox" aria-label={rotulo}>
        {filtradas.map((o) => {
          const escolhida = o.valor === valor;
          const Icone = o.Icone;
          return (
            <button
              key={o.valor}
              type="button"
              role="option"
              aria-selected={escolhida}
              onClick={() => {
                aoEscolher(o.valor);
                fechar();
              }}
              className={`flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${
                escolhida
                  ? 'bg-marca-600/10 text-tinta-900'
                  : 'text-tinta-700 hover:bg-marca-600/[0.07]'
              }`}
            >
              {Icone ? (
                <Icone
                  className={`mt-0.5 size-4 shrink-0 ${escolhida ? 'text-marca-600' : 'text-tinta-500'}`}
                  aria-hidden
                />
              ) : (
                /* Espaço reservado: sem ele, opção sem ícone desalinha do resto. */
                <span className="mt-0.5 size-4 shrink-0" aria-hidden />
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{o.rotulo}</span>
                {o.descricao && (
                  <span className="text-tinta-500 block text-xs leading-snug">
                    {o.descricao}
                  </span>
                )}
              </span>
              {escolhida && (
                <Check className="text-marca-600 mt-0.5 size-4 shrink-0" aria-hidden />
              )}
            </button>
          );
        })}

        {/* Lista vazia diz o que fazer, não só que está vazia. */}
        {filtradas.length === 0 && (
          <p className="text-tinta-500 px-2.5 py-6 text-center text-sm">
            Nada com “{termo.trim()}”.
            <br />
            <span className="text-xs">Tente outro termo ou limpe a consulta.</span>
          </p>
        )}
      </div>
    </>
  );
}

export function SeletorOpcao<T extends string>({
  valor,
  opcoes,
  aoEscolher,
  rotulo,
  IconeGatilho,
  larguraPainel = 'w-72',
  comBusca,
  placeholderBusca = 'Consultar…',
}: {
  valor: T;
  opcoes: readonly OpcaoSeletor<T>[];
  aoEscolher: (valor: T) => void;
  /** Nome acessível do gatilho. Obrigatório: não há rótulo visível. */
  rotulo: string;
  /** Ícone fixo do gatilho, que identifica o campo sem rótulo empilhado. */
  IconeGatilho?: LucideIcon;
  /** O painel é mais largo que o gatilho de propósito — ver o comentário acima. */
  larguraPainel?: string;
  /**
   * Força o campo de consulta em painel curto, ou tira de painel longo. Sem
   * isto, ele entra sozinho a partir de {@link MINIMO_PARA_BUSCA} opções.
   */
  comBusca?: boolean;
  placeholderBusca?: string;
}): JSX.Element {
  const atual = opcoes.find((o) => o.valor === valor) ?? opcoes[0];
  const busca = comBusca ?? opcoes.length >= MINIMO_PARA_BUSCA;

  return (
    <Dropdown
      rotulo={rotulo}
      alinhamento="inicio"
      classeGatilho={`${CONTROLE_BARRA} px-3`}
      classePainel={`${larguraPainel} p-1`}
      gatilho={
        <>
          {IconeGatilho && (
            <IconeGatilho className="text-tinta-500 size-4 shrink-0" aria-hidden />
          )}
          <span className="truncate">{atual?.rotulo}</span>
          <ChevronDown className="text-tinta-500 ml-auto size-4 shrink-0" aria-hidden />
        </>
      }
    >
      {(fechar) => (
        <PainelOpcoes
          opcoes={opcoes}
          valor={valor}
          aoEscolher={aoEscolher}
          rotulo={rotulo}
          comBusca={busca}
          placeholderBusca={placeholderBusca}
          fechar={fechar}
        />
      )}
    </Dropdown>
  );
}
